/**
 * Audit log store — paginated event feed with server-side filters.
 *
 * Filters live in this store so deep-linking (the page reads them from the
 * URL) and the table refresh both pull from one source of truth.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";
import {
  AuditPageSchema,
  type AuditEvent,
  type AuditEventKind,
} from "@/api/schemas/audit";
import { MOCK_AUDIT_EVENTS } from "@/api/fixtures/audit";

export interface AuditFilters {
  /** Free-text search across summary + user + IP. */
  query: string;
  /** Limit to specific event kinds. Empty = all. */
  kinds: AuditEventKind[];
  /** Limit to specific vendors. Empty = all. */
  vendors: Array<"meraki" | "aruba" | "shasta" | "system">;
}

const EMPTY_FILTERS: AuditFilters = { query: "", kinds: [], vendors: [] };

interface State {
  data: AuditEvent[];
  total: number;
  filters: AuditFilters;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  load: () => Promise<void>;
  setFilters: (patch: Partial<AuditFilters>) => void;
  resetFilters: () => void;
}

/** Apply filters client-side against the fixture data — used as fallback. */
function applyFiltersToFixture(events: AuditEvent[], f: AuditFilters): AuditEvent[] {
  const kindSet = new Set(f.kinds);
  const vendorSet = new Set(f.vendors);
  return events.filter((e) => {
    if (f.kinds.length > 0 && !kindSet.has(e.kind)) return false;
    if (f.vendors.length > 0 && (!e.vendor || !vendorSet.has(e.vendor))) return false;
    if (f.query) {
      const q = f.query.toLowerCase();
      const hay = `${e.summary} ${e.user ?? ""} ${e.remoteIp ?? ""} ${e.modelName ?? ""} ${e.recordId ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export const useAuditLog = create<State>((set, get) => ({
  data: [],
  total: 0,
  filters: EMPTY_FILTERS,
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const f = get().filters;
      const raw = await apiGet<unknown>("/audit/events/", {
        q: f.query || undefined,
        kind: f.kinds.length > 0 ? f.kinds.join(",") : undefined,
        vendor: f.vendors.length > 0 ? f.vendors.join(",") : undefined,
      });
      const parsed = AuditPageSchema.parse(raw);
      set({ data: parsed.results, total: parsed.count, status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      const filtered = applyFiltersToFixture(MOCK_AUDIT_EVENTS, get().filters);
      set({
        data: filtered,
        total: filtered.length,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  setFilters: (patch) => {
    set((s) => ({ filters: { ...s.filters, ...patch } }));
    void get().load();
  },

  resetFilters: () => {
    set({ filters: EMPTY_FILTERS });
    void get().load();
  },
}));
